import React from 'react';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import { useParallaxRef } from '@/utils/parallax';
import { useRevealOnce } from '@/hooks/useRevealOnce';
import { WobbleCard } from '@/components/lib/aceternity/WobbleCard';

export default function ServicesGapSection() {
  // Parallax for subtitle
  const subtitleRef = useParallaxRef({
    speed: -0.12,
    axis: 'y',
    maxTranslate: 10,
  });

  // Parallax for H2 (subtle)
  const titleRef = useParallaxRef({
    speed: -0.06,
    axis: 'y',
    maxTranslate: 6,
  });

  // Reveal hook for section entrance
  const sectionReveal = useRevealOnce({ amount: 0.2 });

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0, y: 30 },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
        staggerChildren: 0.15,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: 'easeOut' },
    },
  };

  return (
    <>
      {/* Gradient Divider */}
      <div className="w-full h-8 bg-gradient-to-b from-white via-neutral-900 to-black" />

      {/* Main Section */}
      <section className="relative w-full bg-black text-slate-100 py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <motion.div
              ref={sectionReveal.ref}
              variants={containerVariants}
              initial="hidden"
              animate={sectionReveal.controls}
            >
              {/* Heading Section */}
              <div className="text-center mb-12 md:mb-16">
                <h2
                  ref={titleRef as any}
                  className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-slate-50 mb-6"
                >
                  The gap: why great domains fail to launch
                </h2>
                <p
                  ref={subtitleRef as any}
                  className="text-lg md:text-xl text-slate-300 leading-relaxed max-w-3xl mx-auto"
                >
                  A strong .com is an asset. But on its own, it doesn't bring in clients. Without clear positioning, a credible brand, and a conversion-ready website, even the best domain is just an expensive business card.
                </p>
              </div>

              {/* Two Column Layout */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-start">
                {/* Left Column: Problem Text */}
                <motion.div variants={itemVariants} className="space-y-6">
                  <div>
                    <p className="text-sm font-semibold text-slate-400 uppercase tracking-wide mb-3">
                      The problem
                    </p>
                    <p className="text-slate-200 leading-relaxed mb-6">
                      You did the smart thing and secured a great domain. But you're still not seeing the inbound leads, calls, or booked projects you expected.
                    </p>
                  </div>

                  <ul className="space-y-4">
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-blue-400 mt-0.5 shrink-0" />
                      <span className="text-slate-200">
                        You send people to a half-finished or DIY-looking site you don't feel proud of.
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-blue-400 mt-0.5 shrink-0" />
                      <span className="text-slate-200">
                        Your brand doesn't clearly say who you help or why you're different.
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-blue-400 mt-0.5 shrink-0" />
                      <span className="text-slate-200">
                        Your site looks "fine," but it doesn't actually generate enquiries or booked calls.
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-blue-400 mt-0.5 shrink-0" />
                      <span className="text-slate-200">
                        You're not sure what to put on your pages to turn visitors into clients.
                      </span>
                    </li>
                  </ul>

                  <p className="text-slate-50 font-semibold text-lg pt-2">
                    This isn't a design problem — it's a client-getting system problem.
                  </p>
                </motion.div>

                {/* Right Column: Wobble Card */}
                <motion.div variants={itemVariants} className="flex justify-center lg:justify-start">
                  <div className="w-full max-w-md">
                    <WobbleCard
                      containerClassName="bg-slate-900 border border-slate-800 shadow-2xl min-h-[400px]"
                      className="p-6 md:p-8"
                    >
                      <div className="space-y-4">
                        <div className="inline-block px-3 py-1 bg-slate-800/50 border border-slate-700 rounded-full text-xs font-medium text-slate-300">
                          Common "stuck" pattern
                        </div>
                        <h3 className="text-2xl md:text-3xl font-bold text-slate-50">
                          Great domain. Weak system.
                        </h3>
                        <p className="text-slate-200 leading-relaxed">
                          Your domain is premium, but the brand, site structure, and client path aren't working together. The result: a lot of potential energy, very few booked calls.
                        </p>
                        <ul className="space-y-2 pt-2">
                          <li className="flex items-start gap-2 text-slate-300 text-sm">
                            <span className="text-slate-500 mt-0.5">•</span>
                            <span>No clear "who it's for"</span>
                          </li>
                          <li className="flex items-start gap-2 text-slate-300 text-sm">
                            <span className="text-slate-500 mt-0.5">•</span>
                            <span>Confusing or generic messaging</span>
                          </li>
                          <li className="flex items-start gap-2 text-slate-300 text-sm">
                            <span className="text-slate-500 mt-0.5">•</span>
                            <span>No obvious next step for serious visitors</span>
                          </li>
                        </ul>
                      </div>
                    </WobbleCard>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
}
